import React, { useEffect, useRef, useState } from 'react';
import { usePW } from '@/lib/store';
import { modelsApi } from '@/lib/api';
import { buyTokensStatus, fmtTokens } from '@/lib/tokenEstimate';
import { Coins, CircleDot, Wifi, GitBranch, Save, Sparkles, X } from 'lucide-react';

export const STATUSBAR_LS_KEY = 'loom_statusbar_visible';
export const STATUSBAR_EVENT  = 'loom:statusbar-visible';

function fmt(n) { return n.toLocaleString('en-US'); }

/**
 * CacheStatusDot — small coloured circle that shows live Anthropic prompt-cache
 * state after each AI generation call:
 *   • Grey  (dim)  — no AI operation yet this session
 *   • Red          — cache write: tokens are being written to cache (first call
 *                    or cold start — not yet saving money)
 *   • Green        — cache hit: tokens served from cache (cheaper!)
 *
 * Listens to the global 'cr-cache-status' custom event dispatched by
 * useNovelBuild, useChatStream, and ToolsMenu after each AI call.
 * Only BYOK (native) routes expose real cache counts; the Emergent-key
 * route keeps the dot grey until BYOK credentials are active.
 */
export function CacheStatusDot() {
  const [status, setStatus] = useState(null); // null | 'write' | 'hit'
  const [tokens, setTokens] = useState(0);
  const [pulse, setPulse]   = useState(false);
  // Cumulative session savings (resets on page load, grows with each cache hit).
  const [sessionSaved, setSessionSaved] = useState(0.0);
  const timerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const { read = 0, write = 0, saved_usd = 0 } = e.detail || {};
      const next = read > 0 ? 'hit' : write > 0 ? 'write' : null;
      setStatus(next);
      setTokens(read || write || 0);
      setPulse(true);
      if (saved_usd > 0) setSessionSaved((prev) => parseFloat((prev + saved_usd).toFixed(5)));
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setPulse(false), 1200);
    };
    window.addEventListener('cr-cache-status', handler);
    return () => {
      window.removeEventListener('cr-cache-status', handler);
      clearTimeout(timerRef.current);
    };
  }, []);

  const dotBase = 'h-2 w-2 rounded-full transition-all duration-300 flex-shrink-0';

  if (!status) {
    return (
      <span
        className={`${dotBase} bg-statusbar-foreground/20`}
        title="Anthropic prompt-cache: no AI operation run yet this session"
        data-testid="cache-status-none"
      />
    );
  }

  const isHit   = status === 'hit';
  const colour  = isHit ? 'bg-emerald-400' : 'bg-rose-500';
  const glow    = isHit
    ? 'drop-shadow-[0_0_4px_rgba(52,211,153,0.85)]'
    : 'drop-shadow-[0_0_4px_rgba(244,63,94,0.85)]';
  const savedLabel = sessionSaved > 0 ? ` · $${sessionSaved.toFixed(4)} saved this session` : '';
  const tip = isHit
    ? `Cache HIT — ${Math.round(tokens / 1000)}k tokens served from cache (lower cost)${savedLabel}`
    : `Cache WRITE — ${Math.round(tokens / 1000)}k tokens warming the cache (next call will be cheaper)`;

  return (
    <span className="flex items-center gap-1">
      <span
        className={`${dotBase} ${colour} ${pulse ? `scale-150 ${glow}` : ''}`}
        title={tip}
        data-testid={`cache-status-${status}`}
      />
      {isHit && sessionSaved > 0.0001 && (
        <span
          className="text-[9px] font-mono tabular-nums text-emerald-400 leading-none select-none"
          title={tip}
          data-testid="cache-session-saved"
        >
          ${sessionSaved.toFixed(4)}
        </span>
      )}
    </span>
  );
}

export function StatusBar({ onBuyTokens, onDismiss }) {
  const { tokens, tokenPct, building, buildProgress, project, activeMode, modes, chapters, characters, updateProject } = usePW();
  const mode = modes.find(m => m.id === activeMode);
  const totalWords = chapters.reduce((a, c) => a + (c.words || 0), 0);
  const targetWords = project?.target_words || project?.targetWords || 1;
  const targetPct = Math.min(100, Math.round((totalWords / targetWords) * 100));

  // iter-53n — Token-budget heuristic. Green when comfortable, amber when
  // tight, rose when even one chapter would exhaust the budget.
  const buyStatus = buyTokensStatus(tokens, project?.words_per_chapter || 2500);
  const buyClass = {
    ok:    'bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30',
    low:   'bg-amber-500/20  text-amber-100   hover:bg-amber-500/30',
    empty: 'bg-rose-500/25   text-rose-100    hover:bg-rose-500/35',
  }[buyStatus.level];
  const buyTitle =
    buyStatus.level === 'ok'    ? `~${fmtTokens(buyStatus.remaining)} tokens left — plenty for the next chapter (~${fmtTokens(buyStatus.oneChapter)}).` :
    buyStatus.level === 'low'   ? `Only ~${fmtTokens(buyStatus.remaining)} tokens left — barely covers one more chapter (~${fmtTokens(buyStatus.oneChapter)}). Top up soon.` :
                                  `Only ~${fmtTokens(buyStatus.remaining)} tokens left — not enough for the next chapter (~${fmtTokens(buyStatus.oneChapter)}). Tap to buy more.`;

  const [models, setModels] = useState([]);
  useEffect(() => {
    let cancelled = false;
    modelsApi.list().then((r) => { if (!cancelled) setModels(r.models || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const activeModel = models.find((m) => m.id === (project?.model_id || 'claude-sonnet-4-5'));
  const modelLabel = activeModel?.label || 'Claude Sonnet 4.5';

  // iter55z-e (improvement) — per-model USD estimate for a chapter of
  // the current project's `words_per_chapter`. Backend exposes
  // `cost_multiplier` on each model and the ledger uses a
  // TOKENS_PER_USD_REFERENCE of 60 000 (see budget_service.py). Words →
  // tokens: budget_service records ~1.3 tokens per output word, so we
  // mirror that here so the tooltip matches what the user is billed.
  const TOKENS_PER_USD = 60_000;
  const TOKENS_PER_WORD = 1.3;
  const wordsPerChapter = project?.words_per_chapter || 2500;
  const estChapterUsd = (mult) => {
    const tokens = wordsPerChapter * TOKENS_PER_WORD;
    return (tokens / TOKENS_PER_USD) * (Number(mult) || 1);
  };
  const fmtUsd = (n) => n < 0.10 ? `~$${n.toFixed(3)}` : `~$${n.toFixed(2)}`;
  const modelTooltip = (m) =>
    `${m.label} — ${fmtUsd(estChapterUsd(m.cost_multiplier))} for a ${wordsPerChapter.toLocaleString()}-word chapter`
    + (m.recommended_for ? ` · ${m.recommended_for}` : '');

  // iter55z-e — Global model switcher. Persists per-project so the
  // choice sticks across sessions. Disabled until models load or when
  // no project is active.
  const onModelChange = async (e) => {
    const next = e.target.value;
    if (!project || next === project.model_id) return;
    try {
      await updateProject({ model_id: next, model_inherits_user_default: false });
    } catch (err) {
      // Optimistic UI: usePW.updateProject already rolls back on failure.
      // We intentionally swallow here so the select doesn't jump.
      console.warn('Model switch failed', err);
    }
  };

  return (
    <div className="flex min-h-[1.75rem] flex-wrap items-center justify-between gap-y-0.5 border-t border-chrome-border bg-statusbar px-2 py-0.5 text-[11px] text-statusbar-foreground/90">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 font-medium">
        <span className="flex items-center gap-1.5 whitespace-nowrap"><GitBranch className="h-3 w-3" /> main / draft</span>
        <span className="opacity-60">│</span>
        <span className="flex items-center gap-1.5 whitespace-nowrap"><Save className="h-3 w-3" /> Auto-saved 2s ago</span>
        <span className="opacity-60">│</span>
        <span className="flex items-center gap-1.5 whitespace-nowrap"><Sparkles className="h-3 w-3" /> Mode: <b>{mode?.title}</b></span>
        <span className="opacity-60">│</span>
        <span className="whitespace-nowrap">{characters.length} characters · {chapters.length} chapters</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 font-mono-stat">
        {building && (
          <span className="flex items-center gap-2 whitespace-nowrap">
            <span className="anim-pulse-soft">Rebuilding novel… {buildProgress}%</span>
            <span className="h-1.5 w-28 overflow-hidden rounded-sm bg-statusbar-foreground/20">
              <span className="block h-full bg-statusbar-foreground transition-all" style={{ width: `${buildProgress}%` }} />
            </span>
          </span>
        )}
        <span className="whitespace-nowrap">{fmt(totalWords)} / {fmt(targetWords)} words ({targetPct}%)</span>
        <span className="opacity-60">│</span>
        <button
          onClick={onBuyTokens}
          className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-sm px-1.5 py-0.5 transition-colors ${buyClass}`}
          title={buyTitle}
          data-testid="status-buy-tokens-btn"
          data-token-status={buyStatus.level}
        >
          <Coins className="h-3 w-3 shrink-0" />
          <span>{fmtTokens(tokens.used)} / {fmtTokens(tokens.budget)} tokens ({tokenPct}%)</span>
        </button>
        <span className="opacity-60">│</span>
        {/* iter-cache-dot — Anthropic prompt-cache status indicator */}
        <span className="flex shrink-0 items-center gap-1 whitespace-nowrap" title="Anthropic prompt-cache status">
          <CacheStatusDot />
          <span className="opacity-50 text-[10px]">Cache</span>
        </span>
        <span className="opacity-60">│</span>
        {/* iter55f76 — Model switcher. */}
        {models.length <= 1 ? (
          <span
            className="flex shrink-0 items-center gap-1.5 whitespace-nowrap"
            data-testid="status-active-model"
            title={
              activeModel
                ? modelTooltip(activeModel)
                : `Active model — ${modelLabel}. Click to switch.`
            }
          >
            <Wifi className="h-3 w-3 shrink-0" />
            <span>{modelLabel}</span>
          </span>
        ) : (
          <div
            className="flex shrink-0 items-center gap-1.5 whitespace-nowrap"
            data-testid="status-active-model"
            title={
              activeModel
                ? modelTooltip(activeModel)
                : `Active model — ${modelLabel}. Click to switch.`
            }
          >
            <Wifi className="h-3 w-3 shrink-0" />
            <select
              value={project?.model_id || 'claude-sonnet-4-5'}
              onChange={onModelChange}
              disabled={!project}
              className="cursor-pointer bg-transparent text-[11px] font-medium text-statusbar-foreground/90 outline-none hover:text-statusbar-foreground focus:text-statusbar-foreground disabled:opacity-50"
              data-testid="status-model-switcher"
            >
              {models.map((m) => {
                const label = `${m.label} · ${fmtUsd(estChapterUsd(m.cost_multiplier))}/ch`;
                return React.createElement(
                  'option',
                  {
                    key: m.id,
                    value: m.id,
                    title: modelTooltip(m),
                    className: 'bg-background text-foreground',
                    'data-testid': `status-model-option-${m.id}`,
                  },
                  label,
                );
              })}
            </select>
          </div>
        )}
        {project && <span className="shrink-0 whitespace-nowrap rounded-sm bg-statusbar-foreground/15 px-1 py-0.5 text-[8px] font-semibold uppercase" data-testid="statusbar-model-scope">{project.model_inherits_user_default ? 'Default' : 'Override'}</span>}
        <span className="opacity-60">│</span>
        <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap"><CircleDot className="h-2.5 w-2.5" /> Ln 318, Col 42</span>
        {/* Dismiss strip */}
        <button
          type="button"
          onClick={onDismiss}
          title="Hide info strip (restore in File → Preferences → Appearance)"
          data-testid="statusbar-dismiss-btn"
          className="ml-2 flex h-4 w-4 items-center justify-center rounded text-statusbar-foreground/50 hover:bg-statusbar-foreground/10 hover:text-statusbar-foreground transition-colors"
        >
          <X className="h-2.5 w-2.5" />
        </button>
      </div>
    </div>
  );
}
